import type React from "react";
import type { Event } from "../../types/events";
import { Calendar, Users, Trophy, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

interface EventCardProps {
  event: Event;
}

const EventCard: React.FC<EventCardProps> = ({ event }) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const now = new Date();
  const startDate = event.startDate;
  const endDate = event.endDate;

  const isLive = now >= startDate && now <= endDate;
  const isUpcoming = now < startDate;
  const hasPrizes = event.prizes && event.prizes.length > 0;

  return (
    <div className="flex-shrink-0 w-64">
      <div className="bg-slate-900 rounded-xl overflow-hidden shadow-md border border-slate-800 hover:shadow-lg transition-all duration-200">
        {/* Image */}
        <div className="relative aspect-[4/3] bg-slate-800">
          {event.bannerImage ? (
            <img
              src={event.bannerImage}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Calendar className="w-10 h-10 text-white/50" />
            </div>
          )}

          {/* Status badge */}
          <span
            className={`absolute top-2 left-2 px-2 py-0.5 text-[10px] font-semibold rounded-full ${
              isLive
                ? "bg-red-500"
                : isUpcoming
                  ? "bg-green-500"
                  : "bg-gray-600"
            } text-white`}
          >
            {isLive ? "LIVE" : isUpcoming ? "UPCOMING" : "ENDED"}
          </span>

          {/* Arrow button */}
          <Link
            to={`/events/detail/${event.id}`}
            className="absolute top-2 right-2 p-1 rounded-full bg-slate-800/70 hover:bg-slate-700 transition-all duration-150"
          >
            <ArrowUpRight className="w-3 h-3 text-white" />
          </Link>
        </div>

        {/* Content */}
        <div className="p-3">
          <div className="flex justify-between items-center mb-1 text-[10px] text-slate-400 uppercase">
            <span>
              {event.subtitle?.slice(0, 15) +
                (event.subtitle && event.subtitle.length > 15 ? "..." : "Event")}
            </span>
          </div>

          <h3 className="font-semibold text-white text-sm mb-2 line-clamp-2">
            {event.title}
          </h3>

          <div className="flex justify-between items-center text-[10px] text-slate-300 mb-2">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-blue-400" />
              <span>{formatDate(event.startDate)}</span>
            </div>
            {event.submissionCount && (
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3 text-green-400" />
                <span>
                  {event.submissionCount > 999
                    ? `${(event.submissionCount / 1000).toFixed(1)}k`
                    : event.submissionCount}
                </span>
              </div>
            )}
          </div>

          {hasPrizes && (
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-1 mb-2 text-[10px] flex justify-between items-center">
              <span className="flex items-center gap-1">
                <Trophy className="w-3 h-3 text-yellow-400" /> Prizes
              </span>
              <div className="flex gap-1">
                {event.prizes.map((prize, index) => (
                  <span key={index} className="bg-green-600 text-white px-1 rounded text-[9px]">
                    {prize.reward}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button
            className={`w-full text-[11px] font-medium py-2 rounded-md ${
              isLive
                ? "bg-red-600 hover:bg-red-700 text-white"
                : isUpcoming
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-slate-700 hover:bg-slate-600 text-slate-200"
            }`}
          >
            {isLive ? "Join Live" : isUpcoming ? "Register" : "View Details"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
